import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Send, Loader2, CheckCircle2, AlertCircle, Lightbulb, HelpCircle, Upload } from 'lucide-react';
import { useAIFiller } from '../hooks/useAIFiller';
import { useAIFillerStore } from '../stores/aiFillerStore';
import { FieldValue, FormField } from '../types';
import notify from '../lib/notify';
import { readDocument } from '../services/documentReaderService';

interface AIFillerPanelProps {
  onApply?: (values: FieldValue[]) => void;
}

/**
 * GenieFiller Panel
 * Floating panel for AI-powered form filling.
 */
export const AIFillerPanel: React.FC<AIFillerPanelProps> = ({ onApply }) => {
  const { t } = useTranslation();
  const {
    isOpen,
    isProcessing,
    filledValues,
    error,
    suggestions,
    currentFields,
    processText,
    closePanel,
    setUserText,
  } = useAIFiller();


  const [localText, setLocalText] = useState('');
  const [hasForm, setHasForm] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [shouldAutoApply, setShouldAutoApply] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const { triggerFiller } = useAIFiller();
  const fieldsExtractedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setFilledValues = useAIFillerStore(state => state.setFilledValues);

  // Utility function to fill a single form field
  const fillFormField = useCallback((fieldValue: FieldValue): boolean => {
    // Try multiple selectors to find the input
    const selectors = [
      `[name="${fieldValue.name}"]`,
      `#${fieldValue.name}`,
      `[id="${fieldValue.name}"]`,
    ];

    // Also try with CSS-escaped special characters
    try {
      selectors.push(`[name="${CSS.escape(fieldValue.name)}"]`);
      selectors.push(`[id="${CSS.escape(fieldValue.name)}"]`);
    } catch {
      // CSS.escape might not be available
    }

    let input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null = null;
    for (const selector of selectors) {
      try {
        input = document.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector);
        if (input) break;
      } catch {
        // Invalid selector, continue
      }
    }

    if (!input) {
      console.warn(`[GenieFiller] Could not find input for field "${fieldValue.name}"`);
      return false;
    }

    // Set the value using native setter to trigger React's change detection
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype :
        input.tagName === 'SELECT' ? window.HTMLSelectElement.prototype :
          window.HTMLInputElement.prototype,
      'value'
    )?.set;

    if (input.tagName === 'SELECT') {
      // For select elements, find the option that matches
      const select = input as HTMLSelectElement;
      const options = Array.from(select.options);
      const matchingOption = options.find(opt =>
        opt.value === fieldValue.value ||
        opt.text.toLowerCase().includes(String(fieldValue.value).toLowerCase())
      );
      if (matchingOption) {
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(input, matchingOption.value);
        } else {
          select.value = matchingOption.value;
        }
      }
    } else {
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, String(fieldValue.value));
      } else {
        input.value = String(fieldValue.value);
      }
    }

    // Trigger events for React to detect the change
    const inputEvent = new Event('input', { bubbles: true, cancelable: true });
    input.dispatchEvent(inputEvent);

    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    input.dispatchEvent(changeEvent);

    // Also trigger blur to ensure validation runs
    const blurEvent = new Event('blur', { bubbles: true, cancelable: true });
    input.dispatchEvent(blurEvent);

    console.log(`[GenieFiller] Filled field "${fieldValue.name}" with value:`, fieldValue.value);
    return true;
  }, []);

  // Filter valid values (skip null, undefined, empty strings)
  const filterValidValues = useCallback((values: FieldValue[]): FieldValue[] => {
    return values.filter(fieldValue => {
      const value = fieldValue.value;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed !== '' && trimmed.toLowerCase() !== 'null';
      }
      return true;
    });
  }, []);

  // Auto-apply when filled values are available
  useEffect(() => {
    if (shouldAutoApply && filledValues.length > 0) {
      const validValues = filterValidValues(filledValues);

      let filledCount = 0;
      validValues.forEach((fieldValue) => {
        if (fillFormField(fieldValue)) {
          filledCount++;
        }
      });

      if (onApply) {
        onApply(validValues);
      }

      closePanel();
      setLocalText('');

      if (filledCount > 0) {
        notify.success(filledCount === 1 ? t('aiFiller.fieldsFilledSuccess_one', { count: filledCount }) : t('aiFiller.fieldsFilledSuccess', { count: filledCount }));
      } else {
        notify.warning(t('aiFiller.noFieldFilled'));
      }
      setShouldAutoApply(false);
    }
  }, [filledValues, shouldAutoApply, onApply, closePanel, setLocalText, fillFormField, filterValidValues, t]);

  // Extract form fields from the page
  const extractFormFields = useCallback(() => {
    const fields: FormField[] = [];

    // Get all form inputs
    const inputs = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select'
    );

    inputs.forEach((input) => {
      // Skip if no name or id
      if (!input.name && !input.id) return;

      // Get field label
      let label = '';
      const fieldId = input.id || input.name;

      // Try to find label by for attribute
      const labelElement = document.querySelector(`label[for="${fieldId}"]`);
      if (labelElement) {
        label = labelElement.textContent?.trim() || '';
      } else {
        // Try to find parent label
        const parentLabel = input.closest('label');
        if (parentLabel) {
          label = parentLabel.textContent?.trim() || '';
          // Remove the input value from label text if present
          if (input.value && label.includes(input.value)) {
            label = label.replace(input.value, '').trim();
          }
        }
      }

      // If still no label, try placeholder or aria-label
      if (!label) {
        label = input.getAttribute('placeholder') ||
          input.getAttribute('aria-label') ||
          input.name ||
          input.id ||
          '';
      }

      // Determine field type
      let fieldType = 'text';
      if (input.tagName === 'TEXTAREA') {
        fieldType = 'textarea';
      } else if (input.tagName === 'SELECT') {
        fieldType = 'select';
      } else if (input.type) {
        fieldType = input.type;
      }

      fields.push({
        name: input.name || input.id || `field_${fields.length}`,
        description: label || t('aiFiller.fieldLabel', { name: input.name || input.id }),
        field_type: fieldType,
        required: input.hasAttribute('required'),
        current_value: input.value || null,
      });
    });

    return fields;
  }, [t]);

  // Detect forms on the page (only detection, no extraction yet)
  useEffect(() => {
    const checkForForms = () => {
      const forms = document.querySelectorAll('form');
      const inputs = document.querySelectorAll('input:not([type="hidden"]), textarea, select');
      const hasFormElements = forms.length > 0 || inputs.length >= 2;

      setHasForm(hasFormElements);
      useAIFillerStore.getState().setHasForm(hasFormElements);
    };

    // Initial check
    checkForForms();

    // Re-check when DOM changes (form might be added dynamically)
    const observer = new MutationObserver(checkForForms);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Extract fields when panel opens (only once).
  // Always call triggerFiller — even with an empty list — so the panel never
  // displays stale fields from a previous page.
  useEffect(() => {
    if (isOpen && !fieldsExtractedRef.current) {
      const fields = hasForm ? extractFormFields() : [];
      triggerFiller(fields, t('aiFiller.formDetected'));
      fieldsExtractedRef.current = true;
    }

    // Reset flag when panel closes
    if (!isOpen) {
      fieldsExtractedRef.current = false;
    }
  }, [isOpen, hasForm, extractFormFields, triggerFiller, t]);

  // Don't render if no form detected
  if (!hasForm) {
    return null;
  }

  const handleSubmit = async () => {
    await processText(localText);
    // handleApply will be triggered by useEffect when filledValues updates
  };

  const handleApply = () => {
    if (filledValues.length > 0) {
      const validValues = filterValidValues(filledValues);

      let filledCount = 0;
      validValues.forEach((fieldValue) => {
        if (fillFormField(fieldValue)) {
          filledCount++;
        }
      });

      // Also call callback if provided
      if (onApply) {
        onApply(validValues);
      }

      closePanel();
      setLocalText('');

      // Show success notification with actual fill count
      if (filledCount > 0) {
        notify.success(filledCount === 1 ? t('aiFiller.fieldsFilledSuccess_one', { count: filledCount }) : t('aiFiller.fieldsFilledSuccess', { count: filledCount }));
      } else {
        notify.warning(t('aiFiller.noFieldFilled'));
      }
    }
  };

  const handleClose = () => {
    closePanel();
    setLocalText('');
  };

  // Don't render the panel when not open
  if (!isOpen) {
    return null;
  }

  // Expanded panel
  return (
    <div className="fixed bottom-6 right-6 w-96 bg-white dark:bg-card rounded-lg shadow-2xl
                    border border-gray-200 dark:border-gray-700 z-50
                    animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-4 py-3 rounded-t-lg
                      flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <h3 className="font-semibold">{t('aiFiller.title')}</h3>
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            {showTooltip && (
              <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-white dark:bg-gray-800
                              text-gray-700 dark:text-gray-300 text-xs rounded-lg shadow-xl
                              border border-gray-200 dark:border-gray-700 z-50">
                <p className="font-medium text-gray-900 dark:text-white mb-1">{t('aiFiller.howItWorks')}</p>
                <p>{t('aiFiller.howItWorksDesc')}</p>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="hover:bg-white/20 rounded p-1 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 max-h-[700px] overflow-y-auto">
        {/* File upload section */}
        <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,.doc,.docx"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              setIsUploadingFile(true);
              try {
                // Send file to backend document reader service
                const response = await readDocument(file);

                if (response.success && response.content.trim()) {
                  setLocalText(prev => prev ? prev + '\n\n' + response.content : response.content);
                  setUserText(localText ? localText + '\n\n' + response.content : response.content);
                  notify.success(response.word_count ? t('aiFiller.fileLoadedWords', { name: response.filename, count: response.word_count }) : t('aiFiller.fileLoaded', { name: response.filename }));
                } else {
                  notify.warning(response.error || t('aiFiller.noTextExtracted'));
                }
              } catch (err) {
                console.error('Error reading file:', err);
                notify.error(t('aiFiller.fileReadError'));
              } finally {
                setIsUploadingFile(false);
                // Reset input
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }
            }}
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Upload className="w-4 h-4" />
              <span>{t('aiFiller.importFile')}</span>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingFile || isProcessing}
              className="px-3 py-1.5 text-xs font-medium bg-primary/10
                       text-primary rounded-md hover:bg-primary/20
                       transition-colors disabled:opacity-50"
            >
              {isUploadingFile ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('aiFiller.loading')}
                </span>
              ) : (
                t('aiFiller.browse')
              )}
            </button>
          </div>
        </div>

        {/* No-fields empty state — guards against running the LLM when there is nothing to fill */}
        {currentFields.length === 0 && (
          <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300
                          bg-amber-50 dark:bg-amber-900/20 p-3 rounded border border-amber-100
                          dark:border-amber-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{t('aiFiller.noFieldsToFill', { defaultValue: 'No fillable fields detected on this page. Open a form first, then re-open this panel.' })}</span>
          </div>
        )}

        {/* Fields info */}
        {currentFields.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
            <div className="flex items-center justify-between mb-1">
              <p className="font-medium">{t('aiFiller.fieldsToFill', { count: currentFields.length })}</p>
              {currentFields.length > 3 && (
                <button
                  onClick={() => setShowAllFields(!showAllFields)}
                  className="text-primary hover:text-primary/80 flex items-center gap-1"
                >
                  {showAllFields ? (
                    <>
                      <span>{t('aiFiller.collapse')}</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>{t('aiFiller.showAll')}</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
            <ul className="list-disc list-inside space-y-0.5">
              {(showAllFields ? currentFields : currentFields.slice(0, 3)).map((field, idx) => (
                <li key={idx} className="truncate" title={field.description || field.name}>
                  {field.description || field.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Text input */}
        {currentFields.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('aiFiller.yourText')}
          </label>
          <textarea
            value={localText}
            onChange={(e) => {
              setLocalText(e.target.value);
              setUserText(e.target.value);
            }}
            placeholder={t('aiFiller.placeholder')}
            className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600
                     rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                     focus:ring-2 focus:ring-primary focus:border-transparent
                     resize-none text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
            disabled={isProcessing}
          />
          {/* Character count indicator */}
          <div className="flex justify-end mt-1">
            <span className={`text-xs ${localText.length > 12000
                ? 'text-orange-600 dark:text-orange-400 font-medium'
                : 'text-gray-400 dark:text-gray-500'
              }`}>
              {t('aiFiller.charCount', { count: localText.length })}
              {localText.length > 12000 && ` ${t('aiFiller.truncated')}`}
            </span>
          </div>
        </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400
                          bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-100
                          dark:border-red-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Filled values preview */}
        {filledValues.length > 0 && (() => {
          const validCount = filledValues.filter(field => {
            const value = field.value;
            if (value === null || value === undefined) return false;
            if (typeof value === 'string') {
              const trimmed = value.trim();
              return trimmed !== '' && trimmed.toLowerCase() !== 'null';
            }
            return true;
          }).length;

          return (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{t('aiFiller.extractedValues', { count: validCount })}</span>
                </div>
                <button
                  onClick={() => {
                    setFilledValues([]);
                    setLocalText('');
                  }}
                  className="text-xs px-2 py-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                >
                  {t('aiFiller.cancel')}
                </button>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 bg-green-50 dark:bg-green-900/20 p-2 rounded border border-green-100 dark:border-green-800">
                <p>{t('aiFiller.autoFillInProgress')}</p>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {filledValues
                  .filter(field => {
                    const value = field.value;
                    if (value === null || value === undefined) return false;
                    if (typeof value === 'string') {
                      const trimmed = value.trim();
                      return trimmed !== '' && trimmed.toLowerCase() !== 'null';
                    }
                    return true;
                  })
                  .map((field, idx) => {
                    // Find the corresponding field description from currentFields
                    const fieldInfo = currentFields.find(f => f.name === field.name);
                    const displayName = fieldInfo?.description || field.name;

                    return (
                      <div key={idx} className="text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded
                                           border border-green-100 dark:border-green-800">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-green-900 dark:text-green-300">{displayName}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${field.confidence >= 0.8
                              ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200'
                              : field.confidence >= 0.5
                                ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                                : 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                            }`}>
                            {Math.round(field.confidence * 100)}%
                          </span>
                        </div>
                        <div className="text-gray-700 dark:text-gray-300 mb-1">
                          <span className="font-medium">{t('aiFiller.value')}</span> {String(field.value)}
                        </div>
                        {field.reasoning && (
                          <div className="text-gray-600 dark:text-gray-400 italic">
                            {field.reasoning}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })()}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
              <Lightbulb className="w-4 h-4" />
              <span>{t('aiFiller.suggestions')}</span>
            </div>
            <ul className="text-xs space-y-1">
              {suggestions.map((suggestion, idx) => (
                <li key={idx} className="text-gray-600 dark:text-gray-300 bg-amber-50
                                        dark:bg-amber-900/20 p-2 rounded border border-amber-100
                                        dark:border-amber-800">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex gap-2">
        {filledValues.length > 0 ? (
          <>
            <button
              onClick={handleApply}
              className="flex-1 bg-primary text-primary-foreground px-4 py-2
                       rounded-lg hover:bg-primary/90 transition-all
                       flex items-center justify-center gap-2 font-medium"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('aiFiller.apply')}
            </button>
            <button
              onClick={() => setLocalText('')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('aiFiller.reset')}
            </button>
          </>
        ) : currentFields.length > 0 ? (
          <button
            onClick={handleSubmit}
            disabled={isProcessing || !localText.trim()}
            className="flex-1 bg-primary text-primary-foreground px-4 py-2
                     rounded-lg hover:bg-primary/90 transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2 font-medium"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('aiFiller.analyzing')}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t('aiFiller.analyze')}
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
          >
            {t('global.close', { defaultValue: 'Close' })}
          </button>
        )}
      </div>
    </div>
  );
};
